import { useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { EvaluationSuggestionService } from "@/services/agent/EvaluationSuggestionService";
import { Agent } from "@/types/agent/agent";
import { SuggestedTestCategory } from "@/types/agent/brand-integrity";

interface UseEvaluationSuggestionsResult {
  suggestions: SuggestedTestCategory[];
  isLoading: boolean;
  isGenerating: boolean;
  isStale: boolean;
  error: Error | null;
  regenerate: () => void;
  configHash: string | null;
}

/**
 * Hook to get AI-generated evaluation suggestions for an agent.
 * Automatically detects config changes and triggers regeneration.
 */
export const useEvaluationSuggestions = (
  agent: Agent | null
): UseEvaluationSuggestionsResult => {
  const queryClient = useQueryClient();
  const previousHashRef = useRef<string | null>(null);

  // Calculate current config hash
  const currentConfigHash = useMemo(() => {
    if (!agent) return null;
    return EvaluationSuggestionService.generateConfigHash(agent);
  }, [agent]);

  // Fetch suggestions (will use cache if valid)
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["evaluation-suggestions", agent?.id],
    queryFn: () => EvaluationSuggestionService.getSuggestedTests(agent!),
    enabled: !!agent,
    staleTime: 1000 * 60 * 5, // Consider stale after 5 minutes
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
  });

  // Mutation for force regeneration
  const regenerateMutation = useMutation({
    mutationFn: () => EvaluationSuggestionService.regenerateSuggestions(agent!),
    onSuccess: (newData) => {
      // Update the query cache with new suggestions
      queryClient.setQueryData(["evaluation-suggestions", agent?.id], {
        suggestions: newData.suggestions,
        configHash: newData.configHash,
        cached: false,
        generatedAt: new Date().toISOString(),
      });
    },
  });

  // Detect config changes and auto-regenerate
  useEffect(() => {
    if (!agent || !currentConfigHash || !data?.configHash) return;

    // If config hash changed since last check, regenerate
    if (previousHashRef.current && previousHashRef.current !== currentConfigHash) {
      console.log("Agent config changed, regenerating suggestions...");
      regenerateMutation.mutate();
    }

    previousHashRef.current = currentConfigHash;
  }, [currentConfigHash, agent, data?.configHash]);

  // Determine if suggestions are stale (config changed but not yet regenerated)
  const isStale = useMemo(() => {
    if (!data?.configHash || !currentConfigHash) return false;
    return data.configHash !== currentConfigHash;
  }, [data?.configHash, currentConfigHash]);

  return {
    suggestions: data?.suggestions || [],
    isLoading,
    isGenerating: regenerateMutation.isPending,
    isStale,
    error: error as Error | null,
    regenerate: () => regenerateMutation.mutate(),
    configHash: data?.configHash || null,
  };
};

/**
 * Hook to prefetch suggestions for multiple agents (useful for agent lists)
 */
export const usePrefetchSuggestions = (agents: Agent[]) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    agents.forEach((agent) => {
      queryClient.prefetchQuery({
        queryKey: ["evaluation-suggestions", agent.id],
        queryFn: () => EvaluationSuggestionService.getSuggestedTests(agent),
        staleTime: 1000 * 60 * 5,
      });
    });
  }, [agents, queryClient]);
};
